import {
  getModes, setMode, clearAlarm, reasonMessage,
} from '../mockStore.js';
import { t } from '../i18n.js';

export function renderOverview(root, state, { canControl }) {
  const lang = state.lang;
  const modes = getModes();
  const uncleared = state.alarms.filter((a) => !a.cleared);
  const avgT = (state.env.indoorTemp.reduce((a, b) => a + b, 0) / state.env.indoorTemp.length).toFixed(1);
  const avgH = Math.round(state.env.indoorHumidity.reduce((a, b) => a + b, 0) / state.env.indoorHumidity.length);
  const holdLeft = state.manualHoldUntil
    ? Math.max(0, Math.round((state.manualHoldUntil - Date.now()) / 60000))
    : 0;

  root.innerHTML = `
    <div class="view">
      <h2 class="view-title">${t(lang, 'overview')} — ${state.houseName}</h2>
      <div class="grid grid-3">
        <div class="card">
          <h3>${t(lang, 'houseStatus')}</h3>
          <div class="stat-row"><span class="stat-label">室內均溫</span><span class="stat-value big">${avgT}°C</span></div>
          <div class="stat-row"><span class="stat-label">室外</span><span class="stat-value">${state.env.outdoorTemp.toFixed(1)}°C</span></div>
          <div class="stat-row"><span class="stat-label">室內濕度</span><span class="stat-value">${avgH}%</span></div>
          <div class="stat-row"><span class="stat-label">室外濕度</span><span class="stat-value">${state.env.outdoorHumidity}%</span></div>
          <div class="stat-row"><span class="stat-label">風速 / 雨</span><span class="stat-value">${state.env.windMs.toFixed(1)} m/s · ${state.env.rainMmH} mm/h</span></div>
          <div class="stat-row"><span class="stat-label">CO₂</span><span class="stat-value">${state.env.co2Ppm} ppm</span></div>
          <div class="stat-row"><span class="stat-label">缸水位</span><span class="stat-value">${state.water.tankLevelPct}%</span></div>
        </div>
        <div class="card">
          <h3>${t(lang, 'mode')}</h3>
          <div class="mode-pills controls-only" data-modes>
            ${modes.map((m) => `
              <button type="button" class="mode-pill ${state.mode === m.id ? 'active' : ''} ${m.id === 'typhoon' ? 'typhoon' : ''}" data-mode="${m.id}">
                ${lang === 'en' ? m.labelEn : m.label}
              </button>`).join('')}
          </div>
          <div class="meta" style="margin-top:10px">
            ${t(lang, 'controlMode')}:
            <strong>${state.controlMode === 'auto' ? t(lang, 'auto') : `${t(lang, 'manual')} · ${holdLeft} min`}</strong>
          </div>
          ${state.mode === 'typhoon' ? `<div class="lock-reason">颱風模式：窗已鎖安全位；App <strong>唔可以</strong>遠端強開窗。現場解鎖先。</div>` : ''}
        </div>
        <div class="card">
          <h3>供電／通訊</h3>
          <div class="btn-row" style="margin:0">
            <span class="badge ${state.power.mains === 'ok' ? 'on' : 'off'}">市電 ${state.power.mains}</span>
            <span class="badge ${state.power.ups === 'ok' ? 'on' : 'warn'}">UPS ${state.power.ups}</span>
            <span class="badge off">通訊 ${state.power.comms}</span>
          </div>
          <h3 style="margin-top:12px">硬件互鎖（只顯示）</h3>
          <div class="btn-row" style="margin:0">
            <span class="badge ${state.hardwareLocks.eStop ? 'off' : 'lock'}">急停 ${state.hardwareLocks.eStop ? '觸發' : '正常'}</span>
            <span class="badge ${state.hardwareLocks.rcdTrip ? 'off' : 'lock'}">漏電 RCD</span>
            <span class="badge ${state.hardwareLocks.overload ? 'off' : 'lock'}">過載</span>
            <span class="badge ${state.hardwareLocks.typhoonFieldLock ? 'off' : 'lock'}">現場颱風鎖</span>
          </div>
          <div class="hw-lock">急停／漏電／過載／主斷路器／市電切換 — <strong>App 永遠繞唔過、唔可以遠端 reset</strong>。</div>
        </div>
      </div>

      <div class="grid grid-2" style="margin-top:10px">
        <div class="card">
          <h3>${t(lang, 'alerts')}（未清除 ${uncleared.length}）</h3>
          <ul class="alert-list">
            ${uncleared.length === 0 ? '<li><span class="meta">無未清除警報</span></li>' : ''}
            ${uncleared.map((a) => `
              <li>
                <span class="alert-sev ${a.sev === 'crit' ? 'crit' : a.sev === 'warn' ? 'warn' : 'info'}">${a.sev}</span>
                <div style="flex:1">
                  <div><strong>${a.code}</strong> — ${lang === 'en' ? a.msgEn : a.msg}</div>
                  <div class="meta">${new Date(a.ts).toLocaleString('zh-HK', { hour12: false })}</div>
                  <div class="btn-row controls-only">
                    <button type="button" class="btn sm" data-clear="${a.id}">${t(lang, 'clear')}</button>
                  </div>
                </div>
              </li>`).join('')}
          </ul>
        </div>
        <div class="card">
          <h3>執行器快覽</h3>
          ${state.windows.map((w) => `
            <div class="stat-row">
              <span class="stat-label">${w.name}</span>
              <span class="stat-value">${w.position}% ${w.fault ? '⚠ ' + w.fault : ''} ${w.atLimitClose ? '·限位關' : ''} ${w.atLimitOpen ? '·限位開' : ''}</span>
            </div>`).join('')}
          <div class="stat-row"><span class="stat-label">${state.curtain.name}</span><span class="stat-value">${state.curtain.position}%</span></div>
          <div class="stat-row"><span class="stat-label">白光行人燈</span><span class="stat-value">${state.lights.whiteWalkway.on ? 'ON' : 'OFF'}</span></div>
          <div class="stat-row"><span class="stat-label">紅光補光</span><span class="stat-value">${state.lights.redGrow.on ? 'ON' : 'OFF'}</span></div>
          <div class="stat-row"><span class="stat-label">排風 / 循環 / 冷氣</span><span class="stat-value">${state.fans.exhaust.on ? '排ON' : '排OFF'} · ${state.fans.circulate.on ? '循ON' : '循OFF'} · AC ${state.ac.enabled ? 'EN' : '—'}</span></div>
        </div>
      </div>
      <div id="ov-toast" class="lock-reason" style="display:none;margin-top:10px"></div>
    </div>
  `;

  const toast = root.querySelector('#ov-toast');
  function showReason(r) {
    if (!r || r.ok) { toast.style.display = 'none'; return; }
    toast.style.display = 'block';
    toast.textContent = reasonMessage(r.reason, lang);
  }

  root.querySelectorAll('[data-mode]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!canControl) return;
      showReason(setMode(btn.dataset.mode));
    });
  });
  root.querySelectorAll('[data-clear]').forEach((btn) => {
    btn.addEventListener('click', () => showReason(clearAlarm(btn.dataset.clear)));
  });
}
